import { SameLocationInfo, SameLocationTarget } from 'src/components/dispatch/unassignedDisplay/sameLocationVM';
import { UnassignedItem } from 'src/entities/dispatch';
import { DeliveryListGroupingValues } from 'src/entities/licenseEntity';

import { buildDisplayRotationNumbers, VehicleRowGroup } from './adapter';
import { placeKeyOf } from './placeKeys';

/**
 * 未割当案件ごとに「同じ地点がすでに他車両へ割当済みか」を判定する
 * (logposeTech/log_web_issues#318)。
 *
 * 地点の同一性は `placeKeys.ts` の `placeKeyOf` だけで決める。配車済みカードは
 * `DispatchCard.placeKey` に同じ関数の結果を持っているので、こちらは未割当案件側の
 * キーを同じ関数で作って突き合わせるだけにする。
 *
 * Why not 既存バッジ (duplicateStoreNames) と同じ storeName 一致:
 * 同名で住所の違う拠点が合流するうえ、実データでは逆に「同一座標だが名称が違う」
 * ケースが多く取りこぼす (2025-07-07 の実測で未割当 1342 件のうち placeKey 判定 112 件に対し
 * storeName 判定は 29 件。差の 83 件はすべて座標一致・名称違い)。
 *
 * Why not 判定を配車済みカードどうしにも広げる: 既存の「他車両での取扱あり」バッジと
 * 詳細パネルの警告は storeName 判定のまま据え置く (#318 のスコープ外)。
 */
export function buildSameLocationAssignments(
  items: readonly UnassignedItem[],
  rowGroups: readonly VehicleRowGroup[],
  grouping: DeliveryListGroupingValues,
): Map<string, SameLocationInfo> {
  const result = new Map<string, SameLocationInfo>();
  if (items.length === 0 || rowGroups.length === 0) return result;

  const assigned = collectAssignedTargets(rowGroups);
  if (assigned.size === 0) return result;

  for (const item of items) {
    // fallback に item.id を渡すのは、座標も名称も無い legRun を誰とも一致させないため。
    // カード側の fallback は `op-{operationId}` なので、どちらの一意値とも衝突しない
    const key = placeKeyOf('unloading', item.source, item.id, grouping);
    const targets = assigned.get(key);
    if (targets) result.set(item.id, { targets });
  }

  return result;
}

/**
 * レーン (地点単位表示) 1 行ぶんの情報にまとめる。
 *
 * レーンは同一地点の伝票をまとめたグループなので、配下 5 伝票が同じ 1 台を指す。
 * 素朴に連結すると「割当済 (5)」になり、読み手が台数と取り違える。
 * 重複排除の単位は伝票カードと同じ 行 (車両 × 回転) × 地点名。
 *
 * Why not サンプルのように先頭 1 件だけ拾う: レーンキーは積地→降地のペアなので
 * 降地は同一だが、配下の伝票ごとに割当済みの相手が違いうる (座標が同じで名称の違う
 * 地点が実データに存在する)。先頭だけだと他の相手が落ちる。
 */
export function mergeSameLocationInfos(
  infos: readonly (SameLocationInfo | undefined)[],
): SameLocationInfo | undefined {
  const targets: SameLocationTarget[] = [];
  const seen = new Set<string>();

  for (const info of infos) {
    if (!info) continue;
    for (const target of info.targets) {
      const key = dedupeKeyOf(target);
      if (seen.has(key)) continue;
      seen.add(key);
      targets.push(target);
    }
  }

  return targets.length > 0 ? { targets } : undefined;
}

/**
 * 重複排除の単位。行 (= 車両 × 回転) と地点名で決める。
 * 地点名まで見るのは、同じ行が同一座標の別名称カードを 2 枚持つとき
 * （実データにある）に両方の名前を見せたいため。
 */
const dedupeKeyOf = (t: SameLocationTarget): string => `${t.rowId}-${t.storeName}`;

/** placeKey → 割当済みの訪問。行 × 地点名で重複排除する */
function collectAssignedTargets(
  rowGroups: readonly VehicleRowGroup[],
): Map<string, SameLocationTarget[]> {
  const byPlaceKey = new Map<string, SameLocationTarget[]>();
  const seen = new Map<string, Set<string>>();

  for (const group of rowGroups) {
    // 回転番号は車両リストのバッジと同じ導出にする。生の rotationNumber だと
    // 回転追加の仮想行と FR placeholder の非表示でバッジとずれる
    const rotationNumbers = buildDisplayRotationNumbers(group.rows);

    group.rows.forEach((row, index) => {
      for (const card of row.cards) {
        if (card.type !== 'delivery') continue;
        // 固定ルート由来の仮カードはまだ配車されていないので割当済みに数えない
        if (card.status === 'no-delivery' || card.status === 'fr-template') continue;
        if (!card.placeKey) continue;

        const target: SameLocationTarget = {
          rowId: row.rowId,
          vehicleId: group.vehicle.id,
          vehicleNumber: group.vehicle.vehicleNumber,
          rotationNumber: rotationNumbers[index],
          storeName: card.storeName,
        };

        const dedupeKey = dedupeKeyOf(target);
        const seenForPlace = seen.get(card.placeKey) ?? new Set<string>();
        if (seenForPlace.has(dedupeKey)) continue;
        seenForPlace.add(dedupeKey);
        seen.set(card.placeKey, seenForPlace);

        const bucket = byPlaceKey.get(card.placeKey);
        if (bucket) bucket.push(target);
        else byPlaceKey.set(card.placeKey, [target]);
      }
    });
  }

  return byPlaceKey;
}
